import { LOCATION_ACTION_TYPES } from '../_constants';
import { locationService, invitationsService } from '../_services';
import { uiAlertActions } from '.';
import { invitationsActions } from './invitations.actions';
import { LocationMapper } from '../_mappers';
import { LOCATION_PERMISSIONS, appPermissions } from '../_constants/permissions.constants';
import { LocationModel } from '../_models/LocationModel';
import { i18n } from '../_translations/i18n';
import { genericActions } from './generic.actions';
import { UserMapper } from '../_mappersTS/UserMapper';
import { globalActions } from './global.actions';
import { Selectors } from '../_reducers/app.reducer';
import { locationInfoActions } from './locationInfo.actions';
import { EverouModulesActions } from '../_stores/ModulesStore/EverouModulesActions';
import { InitialUrlStore } from '../../Common/_stores/InitialUrlStore';

export const locationActions = {
    //LOCAL
    stopAdding,
    add,
    selectLocation,
    validateLocation,
    selectLocationFromHeader,
    
    //REMOTE
    list,
    removeSelectedLocation,
    getUsersInLocation,
    getProductsInfo,
    getLocationUsers,
    createLocation,

    updateLocationName,
    updateCompanyName,
    updateCompanyContactEmail,
    updateCompanyLegalId,
    updateCompanyAdditionalInfo,
    updateBarrierMode,
    updateCoordinates,
    updateDetectionZone,

    uploadVideo,
};

function updateLocationName(name, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateLocationName({
                uid: locationUid,
                name,
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
            await dispatch(locationActions.list())
        }
    }
}

function updateCoordinates(latitude, longitude, address, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateCoordinates({
                uid: locationUid,
                latitude,
                longitude,
                address,
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function updateDetectionZone(accuracy, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateDetectionZone({
                uid: locationUid,
                accuracy,
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function updateBarrierMode(barrierMode, locationUid) {
    return async dispatch => {
        return dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateBarrierMode({
                uid: locationUid,
                barrierMode
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function updateCompanyName(companyName, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateCompanyName({
                uid: locationUid,
                companyName,
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function updateCompanyContactEmail(companyContactEmail, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateCompanyContactEmail({
                uid: locationUid,
                companyContactEmail,
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function getProductsInfo(uid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const productsInfo = await locationService.getProductsInfo({
                uid,
            });
            await dispatch(locationInfoActions.updateLocationInfo({ productsInfo }));
        }
    }
}

function updateCompanyLegalId(companyLegalId, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateCompanyLegalId({
                uid: locationUid,
                companyLegalId
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function updateCompanyAdditionalInfo(additionalInfo, locationUid) {
    return async dispatch => {
        dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.updateCompanyAdditionalInfo({
                uid: locationUid,
                additionalInfo
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}

function validateLocation(selectedLocationUid) {
    return (dispatch, getState) => {
        // LOCATIONS LIST IS EXPECTED TO BE UPDATED AT THIS POINT
        let locations = [LocationModel()];
        locations = Selectors.getLocations(getState());

        if (!locations.length) {
            dispatch(globalActions.setPreselectedLocationUid(null));
            dispatch(globalActions.setValidatedLocationUid(null));
            dispatch(locationActions.selectLocation(null));
            dispatch(locationInfoActions.getLocationInfo(null))
            return;
        }

        if (selectedLocationUid === null || !doesLocationExist()) {
            preselectAnotherLocationOrNull();
            return;
        }

        dispatch(globalActions.setValidatedLocationUid(selectedLocationUid));
        return;

        function doesLocationExist() {
            return locations.some(location => location.uid === selectedLocationUid);
        }

        function preselectAnotherLocationOrNull() {
            const location = getAnotherLocation();

            const uidToValidate = location ? location.uid : null;
            dispatch(globalActions.setPreselectedLocationUid(uidToValidate));
            return;

            function getAnotherLocation() {
                const ownerLocation =
                    locations
                    .find(location => location.userType === LOCATION_PERMISSIONS.OWNER)
                ;

                const installerLocation =
                    locations
                    .find(location => location.userType === LOCATION_PERMISSIONS.INSTALLER)
                ;

                const adminLocation =
                    locations
                    .find(location => location.userType === LOCATION_PERMISSIONS.ADMIN)
                ;

                const guestLocation =
                    locations
                    .find(location => location.userType === LOCATION_PERMISSIONS.GUEST)
                ;

                return ownerLocation
                    || installerLocation
                    || adminLocation
                    || guestLocation
                ;
            }
        }
   };
}

function add() {
    return dispatch => {
        dispatch(invitationsActions.clearValidatedInvitation());
        dispatch({
            type: LOCATION_ACTION_TYPES.LOCATIONS_ADD,
        });
    };
}

function stopAdding() {
    return {
        type: LOCATION_ACTION_TYPES.LOCATIONS_STOP_ADDING,
    };
}

function list() {
    return async dispatch => {
        try {
            const serverLocations = await locationService.list();
            const localLocations = LocationMapper.mapAllServerToLocal(serverLocations);
            dispatch(listLocationsSuccess(localLocations));
            return localLocations;
        } catch (error: any) {
            dispatch(listLocationsFailure(error.toString()));
            dispatch(uiAlertActions.error(error.toString()));
            return [];
        }
    };

    function listLocationsSuccess(locations) {
        return {
            type: LOCATION_ACTION_TYPES.LOCATIONS_LIST_SUCCESS,
            locations,
        };
    }

    function listLocationsFailure(error) {
        return {
            type: LOCATION_ACTION_TYPES.LOCATIONS_LIST_FAILURE,
            error,
        };
    }
}

// 201 location not exist, 207 invitation not exist, 802 only the owner can remove
const STALE_LOCATION_ERROR_CODES = [201, 207, 802];

function removeSelectedLocation() {
    return async (dispatch, getState) => {
        try {
            const locationUid = Selectors.getSelectedLocationUid(getState());
            const { userType, invitationUid } = Selectors.getLocations(getState())
                .find(location => location.uid === locationUid);

            // Decide with the permission the server sent in location/list, not
            // with the absence of invitationUid (BAC-296). A non-owner with no
            // cached invitation (stale cache) goes to location/remove too: it
            // answers 200 when nothing is left and 802 when still invited.
            if (userType !== LOCATION_PERMISSIONS.OWNER && invitationUid) {
                await invitationsService.cancelInvitation(invitationUid);
            } else {
                await locationService.removeLocation(locationUid);
            }

            dispatch(success(locationUid));
            dispatch(globalActions.setPreselectedLocationUid(null));
            dispatch(uiAlertActions.success(i18n.t("locations.locationRemoved")));
        } catch (error: any) {
            dispatch(uiAlertActions.error(error.toString()));
            console.error(error);
            // The location or invitation is gone, or it is not ours to remove:
            // reload the list so a ghost entry does not stay there (BAC-296)
            if (STALE_LOCATION_ERROR_CODES.includes(Number(error.name)))
                await dispatch(locationActions.list());
        }

        function success(uid) {
            return {
                type: LOCATION_ACTION_TYPES.LOCATIONS_REMOVE_SUCCESS,
                uid,
            };
        }
    };
}

function selectLocationFromHeader(location = LocationModel()) {
    return dispatch => {
        dispatch(globalActions.setPreselectedLocationUid(location.uid));
    };
}

function selectLocation(locationUid) {
    return (dispatch, getState) => {
        const previousUid = Selectors.getSelectedLocationUid(getState());
        const isSwitchingLocation =
            previousUid && locationUid && previousUid !== locationUid;

        // Switching to a different location: clear the carried-over module
        // selection and the boot-time initial URL so `buildModules` re-picks
        // the per-location default (e.g. Parking when `has_parking` is on).
        // Same pattern `acceptInvitation` uses.
        if (isSwitchingLocation) {
            dispatch(EverouModulesActions._forceSelectModule(null));
            dispatch(InitialUrlStore.actionSet(null));
        }

        dispatch({
            type: LOCATION_ACTION_TYPES.LOCATIONS_SELECT,
            locationUid,
        });
    };
}

function getUsersInLocation() {
    return async (dispatch, getState) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const serverUsers = await locationService.getUsersInLocation(Selectors.getSelectedLocationUid(getState()));
            const localUsers = UserMapper.allServerToLocal(serverUsers);
            dispatch({
                type:LOCATION_ACTION_TYPES.GET_USERS_IN_LOCATION_SUCCESS,
                usersInside: localUsers,
            });
        }
    }
}

function getLocationUsers(locationUid, includeClockinUsers = false) {
    return async (dispatch, getState) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const permission = Selectors.getPermissionSelectedLocation(getState());
            if (!appPermissions.canViewLocationUsers(permission)) {
                return;
            }
            const serverUsers = await locationService.getLocationUsers(
                locationUid || Selectors.getSelectedLocationUid(getState()),
                includeClockinUsers
            );
            const localUsers = UserMapper.allServerToLocal(serverUsers);
            dispatch({
                type:LOCATION_ACTION_TYPES.GET_LOCATION_USERS,
                users: localUsers,
            });
        }
    }
}

function createLocation(name) {
    return async dispatch => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const serverLocation = await locationService.create(name);
            dispatch(
                globalActions.setPreselectedLocationUid(serverLocation.loc_uid)
            );
        }
    }
}

function uploadVideo(video, locationUid) {
    return async dispatch => {
        return dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await locationService.uploadVideo({
                locationUid,
                video
            });
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
        }
    }
}